import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  addImages,
  deleteImage,
  updateId,
  archiveImage,
} from "../redux/imagesSlice";
import axios from "axios";
import { apiURL } from "../config";
import { readAndCompressImage } from "browser-image-resizer";
import closeSVG from "./svg/closeSVG";

function ImageUploader() {
  const images = useSelector((state) => state.images);
  const models = useSelector((state) => state.models);
  const model = useSelector((state) => state.model);
  const dispatch = useDispatch();
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  const handleImageChange = async (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files).map((file) => {
        return { url: URL.createObjectURL(file), file: file };
      });
      // console.log("files: ", files);

      for (const { url } of files) {
        dispatch(
          addImages([
            {
              url,
              status: "uploading",
              imageId: null,
              archived: false,
              deleted: false,
            },
          ])
        );
        setUploadProgress((prev) => ({
          ...prev,
          [url]: 1,
        }));
      }

      for (const { url, file } of files) {
        // console.log("filename: ", file.name, "file size: ", file.size);
        const formData = new FormData();
        const originalName = file.name;
        // console.log("file before resize: ", file);
        let resizedFile = await resizeImage(file, url);
        formData.append("image", resizedFile);
        formData.append("imageURL", JSON.stringify({ url: url, originalName }));
        try {
          const res = await axios.post(
            `${apiURL}/api/message/uploadImage`,
            formData,
            {
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                // console.log("upload percentage: ", percentCompleted);
                setUploadProgress((prev) => ({
                  ...prev,
                  [url]: percentCompleted,
                }));
              },
            }
          );
          console.log("upload success", res);
          dispatch(
            updateId({ url, imageId: res.data.imageId, status: "success" })
          );
        } catch (error) {
          console.log("upload error", error);
          alert("Upload failed");
          dispatch(updateId({ url, imageId: null, status: "failed" }));
        }
      }
    }
  };

  const handleDeleteImage = (url) => {
    // URL.revokeObjectURL(url);
    dispatch(deleteImage(url));
  };

  const handleActiveImage = (url) => {
    dispatch(archiveImage({ url, archive: false }));
  };

  const imagesArchived = images.filter(
    (image) => !image.deleted && image.archived
  );
  const imagesActive = images.filter(
    (image) => !image.deleted && !image.archived
  );

  useEffect(() => {
    const handleFileInputTrigger = (event) => {
      if (event.detail.action === "open") {
        fileInputRef.current.click();
      }
    };

    window.addEventListener("triggerFileInput", handleFileInputTrigger);

    return () => {
      window.removeEventListener("triggerFileInput", handleFileInputTrigger);
    };
  }, []);

  if (models[model] && models[model].vision) {
    return (
      <div className="flex items-center overflow-x-scroll w-full  p-2">
        {imagesActive.length < 5 && (
          <label className="w-20 h-20 mr-2 flex justify-center items-center bg-[hsla(var(--bg-000))] brightness-110 cursor-pointer flex-none">
            <span className="text-white text-sm">+</span>
            <input
              type="file"
              multiple
              onChange={handleImageChange}
              ref={fileInputRef}
              className="hidden"
              accept="image/png, image/jpeg, image/webp"
            />
          </label>
        )}
        {imagesActive.map((image, index) => (
          <div key={image.url} className="relative mr-2 flex-none">
            <img
              src={image.url}
              alt={`upload-preview-${index}`}
              className="w-20 h-20 object-cover "
            />
            <button
              onClick={() => handleDeleteImage(image.url)}
              className="absolute rounded-full bg-red-500 w-4 h-4 -top-1 -right-1  cursor-pointer"
            >
              {closeSVG}
            </button>
            {typeof uploadProgress[image.url] === "number" &&
              uploadProgress[image.url] < 100 && (
                <div className="absolute bottom-0 left-0 w-full bg-gray-200 h-2 translate-y-full ">
                  <div
                    className="bg-blue-500 h-full"
                    style={{ width: `${uploadProgress[image.url]}%` }}
                  ></div>
                </div>
              )}
          </div>
        ))}
        {imagesArchived.length > 0 && (
          <div className="relative ml-3 border h-20 w-fit border-white p-2 rounded-xl flex flex-none gap-3 ">
            {imagesArchived.map((image, index) => (
              <div key={image.url} className="relative flex-none">
                <img
                  src={image.url}
                  alt={`upload-preview-${index}`}
                  className="w-16 h-16 object-cover contrast-50"
                />
                <button
                  onClick={() => handleDeleteImage(image.url)}
                  className="absolute rounded-full bg-red-500 w-4 h-4 -top-1 -right-1  cursor-pointer"
                >
                  {closeSVG}
                </button>

                <button
                  onClick={() => handleActiveImage(image.url)}
                  className="absolute rounded-full bg-green-500 w-4 h-4 -top-1 -left-1  cursor-pointer"
                >
                  {checkSVG}
                </button>
              </div>
            ))}
            <div className="text-xs absolute -top-1 -translate-y-1/2 left-2 px-1 bg-[hsla(var(--bg-100))]">
              Reuse
            </div>
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div>
        <p className="text-sm px-1">This model does not support image input.</p>
      </div>
    );
  }
}

async function resizeImage(file, url) {
  const targetSize = 6 * 1024 * 1024;
  const image = new Image();
  image.src = url;
  await image.decode();
  const { naturalHeight: height, naturalWidth: width } = image;
  console.log(image);
  // console.log(" width: ", width, " height: ", height);
  const scalingFactor = Math.min(Math.sqrt(targetSize / file.size), 1);
  // console.log("scaling factor: ", scalingFactor);

  const resizedFile = await readAndCompressImage(file, {
    quality: 0.9,
    maxWidth: Math.floor(width * scalingFactor),
    maxHeight: Math.floor(height * scalingFactor),
    debug: false,
  });
  // console.log("file after resize", resizedFile);
  return resizedFile;
}

const checkSVG = (
  <svg
    width="12"
    height="12"
    viewBox="0 0 1024 1024"
    xmlns="http://www.w3.org/2000/svg"
    fill="#000000"
    className="pointer-events-none translate-x-[2px]"
  >
    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
    <g
      id="SVGRepo_tracerCarrier"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></g>
    <g id="SVGRepo_iconCarrier">
      <path
        fill="#ffffff"
        d="M77.248 415.04a64 64 0 0 1 90.496 0l226.304 226.304L846.528 188.8a64 64 0 1 1 90.56 90.496l-543.04 543.04-316.8-316.8a64 64 0 0 1 0-90.496z"
      ></path>
    </g>
  </svg>
);
export default ImageUploader;
